import { useEffect, useState } from 'react'
import { Mail, X, Check, AlertCircle, Loader2 } from 'lucide-react'
import { useAuth } from './useAuth'

interface SignInDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * Magic-link sign-in dialog.
 *
 * UX contract:
 *   - One field (email), one action ("Send sign-in link"), no passwords.
 *   - Pressing Enter submits.
 *   - On success: form is replaced with a "Check your email" confirmation.
 *   - On error: inline error under the field, focus returns to the input.
 *   - Escape closes the dialog (matches ConfirmDialog convention).
 *   - **Auto-closes when authentication succeeds in any tab.** A user can
 *     submit the form here, switch to email, click the magic link (which
 *     opens in a sibling tab/window), and when they return to this tab
 *     the dialog dismisses itself — no manual "Close" tap required. This
 *     matches the UX you get from sites like Slack / Substack / Notion.
 *
 * Auth concerns (rate limits, allowlist checks, the actual email send)
 * live in `useAuth.signIn` — this component is purely UI.
 */
export function SignInDialog({ open, onClose }: SignInDialogProps) {
  const { signIn, verifyOtpCode, signInError, magicLinkSent, isAuthenticated, clearError } =
    useAuth()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // v0.8.29 — paste-the-code path. Codes are sent in the same email as the
  // magic link (when the Supabase template includes `{{ .Token }}`) and are
  // immune to link-scanning prefetches that consume one-shot magic links.
  const [code, setCode] = useState('')
  const [verifyingCode, setVerifyingCode] = useState(false)

  // Reset transient state whenever the dialog reopens so a previous
  // failure / "check your email" doesn't leak into the next visit.
  useEffect(() => {
    if (open) {
      setEmail('')
      setSubmitting(false)
      setCode('')
      setVerifyingCode(false)
      clearError()
    }
  }, [open, clearError])

  // Cross-tab auto-dismiss. If `isAuthenticated` flips to true while we're
  // open (because the user signed in via the magic link in a sibling tab
  // and AuthProvider's BroadcastChannel / visibilitychange listener
  // synced the session back here), close the dialog. The Header /
  // RootRouter / etc. immediately reflect signed-in UI so the user sees
  // a real transition rather than being stuck on "Check your email."
  useEffect(() => {
    if (open && isAuthenticated) {
      onClose()
    }
  }, [open, isAuthenticated, onClose])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting || magicLinkSent) return
    setSubmitting(true)
    try {
      await signIn(email)
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (verifyingCode) return
    setVerifyingCode(true)
    try {
      await verifyOtpCode(email, code)
      // On success the existing `isAuthenticated` watcher above auto-closes
      // the dialog. On failure `signInError` is set by the hook and we
      // render it below the code input.
    } finally {
      setVerifyingCode(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl"
      >
        <div className="flex items-start gap-3 p-5 border-b border-slate-800">
          <Mail className="w-6 h-6 shrink-0 text-blue-400" />
          <div className="flex-1 min-w-0">
            <h2 id="signin-title" className="font-semibold text-white text-base">
              Sign in to DebtOS
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              We’ll email you a one-time sign-in link. No password to remember.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {magicLinkSent ? (
          <div className="p-5 text-sm text-slate-300 leading-relaxed space-y-3">
            <div className="flex items-start gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <Check className="w-5 h-5 shrink-0 text-emerald-400 mt-0.5" />
              <div>
                <div className="font-medium text-emerald-300">Check your email</div>
                <div className="text-emerald-200/80 text-xs mt-1">
                  We sent a sign-in link to <span className="font-mono">{email}</span>. It expires
                  in 1&nbsp;hour.
                </div>
              </div>
            </div>

            {/* v0.8.29 — paste-the-code alternative to the magic link.
                Outlook Safe Links and similar email scanners consume one-shot
                magic-link codes by fetching the URL before the user's click;
                the 6-digit OTP in the same email is paste-only and immune to
                that prefetch. Requires the Supabase email template to include
                `{{ .Token }}` — otherwise users see only the link in their
                inbox and this input goes unused. */}
            <form onSubmit={handleVerifyCode} className="border-t border-slate-800 pt-3 space-y-2">
              <label className="block">
                <span className="text-xs font-medium text-slate-300">
                  Or paste the code from the email
                </span>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Use this if your email client (Outlook Safe Links, corporate scanners) breaks the
                  link. Codes can&rsquo;t be consumed by URL prefetches. The code is typically 6 or
                  8 digits depending on the project&rsquo;s Supabase config.
                </div>
              </label>
              <div className="flex gap-2">
                {/* v0.8.30 — accept 6–10 digits. Supabase OTP length is
                    configurable per project; the founder's project sends 8-digit
                    codes. Pre-fix the input was hard-locked to 6 so 8-digit
                    codes were truncated at the boundary and verification failed. */}
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6,10}"
                  maxLength={10}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="00000000"
                  className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 font-mono tracking-widest text-center focus:outline-none focus:border-blue-500"
                />
                <button
                  type="submit"
                  disabled={code.length < 6 || verifyingCode}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 disabled:text-blue-300 text-white transition-colors flex items-center gap-2 shrink-0"
                >
                  {verifyingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Verify
                </button>
              </div>
              {signInError ? (
                <div
                  role="alert"
                  className="flex items-start gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-md text-xs text-red-200"
                >
                  <AlertCircle className="w-4 h-4 shrink-0 text-red-400 mt-0.5" />
                  <span>{signInError}</span>
                </div>
              ) : null}
            </form>

            <details className="text-xs text-slate-400 group">
              <summary className="cursor-pointer hover:text-slate-200 select-none">
                Link not transitioning this page?
              </summary>
              <div className="mt-2 pl-3 border-l border-slate-700 space-y-2 leading-relaxed">
                <p>
                  When you click the link, your email app might open it in its own browser instead
                  of the main one — which means this tab can&rsquo;t see the sign-in.
                </p>
                <p>
                  <strong className="text-slate-200">Easiest fix:</strong> long-press the link in
                  your email and choose <em>Open in browser</em> / <em>Copy link</em>, then paste it
                  into your normal Chrome / Safari. Or paste the 6-digit code above.
                </p>
                <p>
                  Already signed in elsewhere?{' '}
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Reload this tab
                  </button>{' '}
                  to pick up the session.
                </p>
              </div>
            </details>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-300">Email address</span>
                <input
                  type="email"
                  autoFocus
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </label>
              {signInError ? (
                <div
                  role="alert"
                  className="flex items-start gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-md text-xs text-red-200"
                >
                  <AlertCircle className="w-4 h-4 shrink-0 text-red-400 mt-0.5" />
                  <span>{signInError}</span>
                </div>
              ) : null}
            </div>
            <div className="flex gap-2 p-4 border-t border-slate-800 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !email.trim()}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 disabled:text-blue-300 text-white transition-colors flex items-center gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Send sign-in link
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
